import UserService from "@/services/api/UserService";
import useGlobalStore from "@/services/store/useGlobalStore";
import { authenticationLocalStorage } from "@/utils/constants/localStorage";
import { addColorToUsers, mapUsersByGroupId, mapUsersById, mapUsersByLabel } from "@/utils/functions/user";
import { useMemo } from "react";

const useUsers = () => {
  const { users, setUsers, selectedUsers, traces } = useGlobalStore((state) => state);

  const usersByGroup = mapUsersByGroupId(users);
  const usersWithColor = addColorToUsers(users, usersByGroup);
  const usersById: Dic<UserWithColor> = mapUsersById(usersWithColor);
  const usersByLabel: Dic<User> = mapUsersByLabel(usersWithColor);
  const usersByGroupId: Dic<number[]> = mapUsersByGroupId(usersWithColor);

  const getUserTraces = (user: User) => traces.filter((trace) => trace.userId === user.id);

  const addTraceToUsers = (users: User[]) => {
    return users.map((user) => {
      if (!user.traces) return { ...user, traces: getUserTraces(user) };
      return user;
    });
  };

  const getUsers = (retrieveTraces = false) => {
    return new Promise((resolve) => {
      UserService.getUsers(retrieveTraces)
        .then((res) => {
          const users = addTraceToUsers(res);
          const usersByGroup = mapUsersByGroupId(users);
          setUsers(addColorToUsers(users, usersByGroup));
          return res;
        })
        .then(resolve);
    });
  };

  const createUser = (userLabel: string, companyId: string, role: UserRole, password: string): Promise<User> => {
    return UserService.createUser(userLabel, password, companyId, role);
  };

  const deleteUser = (userId: number): Promise<void> => {
    return UserService.deleteUser(userId);
  };

  const updateUserConfig = (username: string, config: UpdateUserConfigDTO): Promise<UserConfigDTO> => {
    return UserService.updateUserConfig(username, config);
  };

  const updateUserPermissions = (username: string, permissions: FeaturePermission[]): Promise<User> => {
    return UserService.updateUserPermissions(username, permissions);
  };
  const changeUserCompany = (username: string, companyId: string): Promise<User> => {
    return UserService.changeUserCompany(username, companyId);
  };

  const setGPSValue = (userId: number, value: boolean): Promise<unknown> => {
    const user = usersById[userId];
    return new Promise((resolve) => {
      UserService.updateUser(user.username, user.leader, value)
        .then(() => getUsers())
        .then(resolve);
    });
  };

  const updateUserFromWS = (wsUser: WSUser) => {
    const updatedUsers = addTraceToUsers(
      users.map((user) => {
        if (user.id === wsUser.id) {
          return {
            ...user,
            mission: wsUser.mission,
            coordinates: wsUser.coordinates ?? null,
            traces: wsUser.traces ?? user.traces,
            status: wsUser.status ?? user.status,
            isGPSEnabled: wsUser.isGPSEnabled,
            leader: wsUser.leader,
            alias: wsUser.name ?? user.alias,
          };
        }
        return user;
      })
    );

    const usersByGroup = mapUsersByGroupId(updatedUsers);
    setUsers(addColorToUsers(updatedUsers, usersByGroup));
  };

  const supervisor = useMemo(() => {
    const {loggedUser} = authenticationLocalStorage;
    if (loggedUser && (loggedUser.role === "SUPERVISOR" || loggedUser.role === "ADMIN")) {
      return loggedUser as unknown as UserWithColor;
    }
    return null;
  }, []);

  return {
    updateUserFromWS,
    setGPSValue,
    getUsers,
    users,
    supervisor,
    usersById,
    usersByLabel,
    usersByGroupId,
    selectedUsers,
    createUser,
    deleteUser,
    updateUserConfig,
    updateUserPermissions,
    changeUserCompany,
  };
};

export default useUsers;
